import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { Link } from 'react-router-dom'
import { Select, Tooltip } from 'antd'
import { get } from 'lodash'
import { segmentApi } from '@edulastic/api'
import { FlexContainer, captureSentryException } from '@edulastic/common'
import {
  IconPDLSparkle,
  IconEditMetaData,
  IconClose,
  IconCheck,
  IconEye,
} from '@edulastic/icons'
import StandardsModal from '../../../assessment/containers/QuestionMetadata/StandardsModal'
import { updateRubricDataAction } from '../../GradingRubric/ducks'
import {
  getBulkSuggestionsSelector,
  getExistingEditByQuestionIdSelector,
  updateBulkSuggestionAction,
  updateExistingMetadataEditAction,
  saveItemStandardAction,
  setBulkSuggestionsAction,
} from '../ducks/bulkQuestionMetadata'
import { useRubricBrowseModal } from './ItemRubricStatus'
import { DIFFICULTY_OPTIONS, DOK_OPTIONS } from '../constants/form'
import {
  getCurriculumsListSelector,
  standardsSelector,
} from '../../src/selectors/dictionaries'
import { getDictStandardsForCurriculumAction } from '../../src/actions/dictionaries'
import {
  getInterestedGradesSelector,
  getInterestedSubjectsSelector,
  getAllInterestedCurriculumsSelector,
} from '../../src/selectors/user'
import {
  getTestEntitySelector,
  getTestItemsSelector,
} from '../../TestPage/ducks'
import {
  buildPdlStandardProps,
  getStandardCurriculumData,
  getStandardIdsToLog,
  hasValidStandardSuggestion,
  getEffectiveStandards,
  hasBulkSuggestion,
  getStandardSetOptions,
  groupStandardsBySet,
  getVisibleStandardGroups,
} from '../utils/AIQuestionAssistance'
import { shouldShowPearAssistRubric } from '../utils/rubricApply'
import {
  StyledMetadataEditorRow,
  StyledMetadataEditorRowWithLabel,
  StyledMetadataEditorWithLabelRow,
  StyledMetadataQuestionSubLabel,
  StyledMetadataEditorFields,
  StyledMetadataStandardsField,
  StyledMetadataStandardsContent,
  StyledMetadataStandardsValueRow,
  StyledMetadataStandardsList,
  StyledMetadataStandardRow,
  StyledMetadataStandardValueText,
  StyledStandardValueTooltipWrap,
  StyledMetadataDifficultyField,
  StyledMetadataDokField,
  StyledMetadataRubricField,
  StyledRubricActionButton,
  StyledMetadataEditorActions,
  StyledMetadataFieldLabel,
  StyledNoStandardsText,
  StyledStandardsEditButton,
  StyledMetadataEditorSelect,
  StyledMetadataEditorSaveButton,
  StyledMetadataEditorClose,
  StyledMetadataStandardSetGroup,
  StyledMetadataStandardSetName,
} from './styled'
import { StyledStandardMoreCount, StyledEmptyStateText } from '../common/styled'

const dokOptions = DOK_OPTIONS.filter((option) => option.value !== '')
const difficultyOptions = DIFFICULTY_OPTIONS.filter(
  (option) => option.value !== ''
)

const MAX_VISIBLE_STANDARDS = 2

const formatStandardDisplayText = (standard) => {
  const identifier = standard?.identifier || standard?.name || ''
  const description = standard?.description || ''
  return `${identifier}${description ? ` (${description})` : ''}`
}

const StandardValueDisplay = ({ standard, romanLabel }) => {
  const lineText = formatStandardDisplayText(standard)
  const displayText = romanLabel ? `${romanLabel} ${lineText}` : lineText

  return (
    <StyledStandardValueTooltipWrap>
      <Tooltip title={standard?.description || standard?.name || ''}>
        <StyledMetadataStandardValueText>
          {displayText}
        </StyledMetadataStandardValueText>
      </Tooltip>
    </StyledStandardValueTooltipWrap>
  )
}

const trackMetadataSuggestionEdited = (payload) => {
  try {
    segmentApi.genericEventTrack(
      'AITestQuestionsMetadata:MetadataSuggestionEdited',
      payload,
      { attachUserData: true }
    )
  } catch (error) {
    captureSentryException(error, {
      errorMessage:
        'AI Test Questions Metadata: Error tracking MetadataSuggestionEdited event',
      sessionId: window?.sessionStorage?.kid,
    })
  }
}

const ItemBulkMetadataEditPanel = ({
  item,
  test,
  suggestions,
  existingEditByQuestionId,
  updateSuggestion,
  updateExistingEdit,
  saveItem,
  closeModal,
  onCancelExistingQuestionEdit,
  allCurriculums,
  interestedCurriculums,
  interestedSubjects,
  interestedGrades,
  getCurriculumStandards,
  curriculumStandardsLoading,
  qid,
  updateRubricData,
  setBulkSuggestions,
  testItems,
  isExistingQuestionEdit = false,
  questionSubLabel,
}) => {
  const { _id: testId } = test || {}
  const itemId = item?._id
  const questions = useMemo(() => get(item, 'data.questions', []), [item])

  const trackMetadataSuggestionEditedEvent = useCallback(
    (payload) => {
      if (isExistingQuestionEdit) return
      trackMetadataSuggestionEdited(payload)
    },
    [isExistingQuestionEdit]
  )

  const getSuggestionForQuestion = useCallback(
    (questionId) =>
      suggestions?.[questionId] ?? existingEditByQuestionId?.[questionId],
    [suggestions, existingEditByQuestionId]
  )

  const updateMetadataEdit = useCallback(
    (questionId, updates) => {
      if (hasBulkSuggestion(suggestions?.[questionId])) {
        updateSuggestion({ questionId, updates })
      } else if (isExistingQuestionEdit) {
        updateExistingEdit({ questionId, patch: updates })
      }
    },
    [suggestions, updateSuggestion, updateExistingEdit, isExistingQuestionEdit]
  )

  const effectiveSuggestions = useMemo(
    () => ({ ...existingEditByQuestionId, ...suggestions }),
    [existingEditByQuestionId, suggestions]
  )

  const applySuggestionUpdates = useCallback(
    (suggestionUpdates) => {
      if (!suggestionUpdates || !Object.keys(suggestionUpdates).length) return
      if (isExistingQuestionEdit) {
        Object.entries(suggestionUpdates).forEach(([questionId, patch]) => {
          if (hasBulkSuggestion(suggestions?.[questionId])) {
            updateSuggestion({ questionId, updates: patch })
          } else {
            updateExistingEdit({ questionId, patch })
          }
        })
      } else {
        setBulkSuggestions(suggestionUpdates)
      }
    },
    [
      isExistingQuestionEdit,
      suggestions,
      updateSuggestion,
      updateExistingEdit,
      setBulkSuggestions,
    ]
  )

  const editableQuestions = useMemo(() => {
    const withSuggestions = (questions || []).filter((q) => {
      const suggestion = getSuggestionForQuestion(q?.id)
      return (
        hasValidStandardSuggestion(suggestion) ||
        Boolean(existingEditByQuestionId?.[q?.id])
      )
    })
    if (qid && withSuggestions?.length) {
      return withSuggestions.filter((q) => q.id === qid) || []
    }
    return withSuggestions || []
  }, [
    questions,
    suggestions,
    existingEditByQuestionId,
    qid,
    getSuggestionForQuestion,
  ])

  const [
    showStandardsSelectionModal,
    setShowStandardsSelectionModal,
  ] = useState(false)
  const [standardsQuestionId, setStandardsQuestionId] = useState(null)
  const [editStandardId, setEditStandardId] = useState(null)
  const [selectedStandardSetId, setSelectedStandardSetId] = useState(null)

  const {
    openBrowseModal,
    loading: rubricUiLoading,
    browseModal,
  } = useRubricBrowseModal({
    updateRubricData,
    setBulkSuggestions: applySuggestionUpdates,
    testItems,
    suggestionsByQuestionId: effectiveSuggestions,
  })

  const defaultGrades = interestedGrades?.length > 0 ? interestedGrades : []

  const { standardSetOptions } = useMemo(
    () => getStandardSetOptions({ interestedCurriculums, interestedSubjects }),
    [interestedCurriculums, interestedSubjects]
  )

  const standardsModalQuestion = useMemo(
    () =>
      editableQuestions.find(
        (question) => question.id === standardsQuestionId
      ) || null,
    [editableQuestions, standardsQuestionId]
  )

  const standardsSelectionModalProps = useMemo(() => {
    if (!standardsModalQuestion) {
      return null
    }

    const suggestion =
      getSuggestionForQuestion(standardsModalQuestion?.id) || {}
    const selectedStandards = getEffectiveStandards(suggestion)
    let firstStandard = selectedStandards?.[0]
    if (editStandardId) {
      const currentEditStandard = selectedStandards.find(
        (standard) => standard?._id === editStandardId
      )
      if (currentEditStandard) {
        firstStandard = currentEditStandard
      }
    }
    // Question has no standards yet: open on the standard set picked from the dropdown
    if (!firstStandard && selectedStandardSetId) {
      firstStandard = { curriculumId: selectedStandardSetId }
    }
    const curriculum = getStandardCurriculumData(firstStandard, allCurriculums)
    const { subject, _id: curriculumId } = curriculum || {}

    return {
      selectedStandards,
      defaultStandard: {
        curriculum,
        id: curriculumId,
      },
      defaultSubject: subject,
      pdlStandardProps: buildPdlStandardProps(curriculum, defaultGrades),
    }
  }, [
    standardsModalQuestion,
    getSuggestionForQuestion,
    allCurriculums,
    defaultGrades,
    editStandardId,
    selectedStandardSetId,
  ])

  const handleViewRubric = useCallback(
    (question) => {
      const suggestion = getSuggestionForQuestion(question?.id)
      openBrowseModal({
        rubricId: suggestion?.rubricId || null,
        itemId,
        questionId: question?.id,
      })
    },
    [openBrowseModal, getSuggestionForQuestion, itemId]
  )

  const handlePanelClose = () => {
    if (isExistingQuestionEdit) {
      onCancelExistingQuestionEdit?.()
      return
    }
    closeModal?.()
  }

  if (!editableQuestions.length) return null

  const questionIds = editableQuestions.map((q) => q?.id).filter(Boolean)

  const handleShowStandardsSelectionModal = (value) => {
    setShowStandardsSelectionModal(value)
    if (!value) {
      setStandardsQuestionId(null)
      setSelectedStandardSetId(null)
    }
  }

  const handleOpenStandardsSelectionModal = (questionId, standardId) => {
    setStandardsQuestionId(questionId)
    setEditStandardId(standardId)
    setShowStandardsSelectionModal(true)
  }

  const handleStandardSetChange = (questionId) => (curriculumId) => {
    setSelectedStandardSetId(curriculumId)
    handleOpenStandardsSelectionModal(questionId)
  }

  const handleStandardsApply = (data = {}) => {
    if (!standardsQuestionId) return

    const selectedStandards = data?.eloStandards || []
    handleShowStandardsSelectionModal(false)

    if (!selectedStandards.length) {
      return
    }

    const suggestion = getSuggestionForQuestion(standardsQuestionId) || {}

    if (isExistingQuestionEdit) {
      updateMetadataEdit(standardsQuestionId, { standards: selectedStandards })
    } else {
      updateSuggestion({
        questionId: standardsQuestionId,
        updates: { standards: selectedStandards },
      })
    }

    saveItem({ itemId, questionIds, isExistingQuestionEdit })
    closeModal?.()

    try {
      const standardsEdited = !selectedStandards.some(
        (standard) => standard?._id === suggestion?.standard?._id
      )

      if (standardsEdited) {
        trackMetadataSuggestionEditedEvent({
          testId,
          itemId,
          questionId: standardsQuestionId,
          standardsEdited: true,
          updatedStandardIds: getStandardIdsToLog(selectedStandards),
        })
      }
    } catch (error) {
      captureSentryException(error, {
        errorMessage:
          'AI Test Questions Metadata: Error tracking standards metadata suggestion edit',
        testId,
        itemId,
        questionId: standardsQuestionId,
        sessionId: window?.sessionStorage?.kid,
      })
    }
  }

  const handleDokChange = (questionId) => (value) => {
    const suggestedDok = getSuggestionForQuestion(questionId)?.dok ?? null
    if (value === suggestedDok) return

    updateMetadataEdit(questionId, { dok: value })
    trackMetadataSuggestionEditedEvent({
      testId,
      itemId,
      questionId,
      dokEdited: true,
      updatedDok: value,
    })
  }

  const handleDifficultyChange = (questionId) => (value) => {
    const suggestedDifficulty =
      getSuggestionForQuestion(questionId)?.difficulty ?? null
    if (value === suggestedDifficulty) return

    updateMetadataEdit(questionId, { difficulty: value })
    trackMetadataSuggestionEditedEvent({
      testId,
      itemId,
      questionId,
      difficultyEdited: true,
      updatedDifficulty: value,
    })
  }

  const handleSave = () => {
    saveItem({ itemId, questionIds, isExistingQuestionEdit })
    closeModal?.()
  }

  return (
    <>
      {editableQuestions.map((question) => {
        const suggestion = getSuggestionForQuestion(question?.id) || {}
        const selectedStandards = getEffectiveStandards(suggestion)
        const dok = suggestion.dok || undefined
        const difficulty = suggestion.difficulty || undefined
        const difficultyFieldId = `itemBulkMetadataEditPanelDifficulty-${question.id}`
        const dokFieldId = `itemBulkMetadataEditPanelDok-${question.id}`
        const standardSetFieldId = `itemBulkMetadataEditPanelStandardSet-${question.id}`
        const showStandardSetSelect =
          isExistingQuestionEdit && !selectedStandards.length
        const rubricName = suggestion?.rubricName || ''
        const showRubricActions = shouldShowPearAssistRubric(suggestion, {
          isExistingQuestionEdit,
        })

        const showPearAssistIcon =
          !isExistingQuestionEdit ||
          hasBulkSuggestion(suggestions?.[question.id])
        const visibleStandardGroups = getVisibleStandardGroups(
          groupStandardsBySet(selectedStandards, allCurriculums),
          MAX_VISIBLE_STANDARDS
        )
        const hiddenStandardsCount = Math.max(
          0,
          selectedStandards.length - MAX_VISIBLE_STANDARDS
        )
        const showRomanLabels = selectedStandards.length > 1
        const MetadataEditorRow = questionSubLabel
          ? StyledMetadataEditorRowWithLabel
          : StyledMetadataEditorRow
        const rowPadding = isExistingQuestionEdit
          ? 'box-sizing: border-box; padding: 0 !important;'
          : 'box-sizing: border-box; padding: 0 20px !important;'

        const metadataEditorRow = (
          <MetadataEditorRow data-cy="itemBulkMetadataEditPanelRow">
            <StyledMetadataEditorFields>
              <StyledMetadataStandardsField>
                {showPearAssistIcon && (
                  <IconPDLSparkle width={24} height={24} aria-hidden="true" />
                )}
                <StyledMetadataStandardsContent>
                  {showStandardSetSelect ? (
                    <>
                      <StyledMetadataFieldLabel htmlFor={standardSetFieldId}>
                        Standard Set
                      </StyledMetadataFieldLabel>
                      {standardSetOptions?.length > 0 ? (
                        <StyledMetadataEditorSelect
                          id={standardSetFieldId}
                          data-cy="itemBulkMetadataEditPanelStandardSet"
                          placeholder="Select Standard Set"
                          value={
                            standardsQuestionId === question.id
                              ? selectedStandardSetId ?? undefined
                              : undefined
                          }
                          getPopupContainer={() => document.body}
                          dropdownStyle={{ zIndex: 2000 }}
                          dropdownMatchSelectWidth={false}
                          onChange={handleStandardSetChange(question.id)}
                        >
                          {standardSetOptions.map((curriculum) => (
                            <Select.Option
                              key={curriculum._id}
                              value={curriculum._id}
                            >
                              {curriculum.name || curriculum.curriculum}
                            </Select.Option>
                          ))}
                        </StyledMetadataEditorSelect>
                      ) : (
                        <StyledEmptyStateText
                          color="#262626"
                          marginTop="0px"
                          data-cy="itemBulkMetadataEditPanelStandardSetEmptyText"
                        >
                          Please add standard set, grade, and subject in the{' '}
                          <Link to="/author/profile">profile page</Link>.
                        </StyledEmptyStateText>
                      )}
                    </>
                  ) : (
                    <>
                      <StyledMetadataFieldLabel>
                        Standards
                      </StyledMetadataFieldLabel>
                      <StyledMetadataStandardsList data-cy="itemBulkMetadataEditPanelStandardValue">
                        {visibleStandardGroups.map(
                          ({ key, name, standards, startIndex }) => (
                            <StyledMetadataStandardSetGroup
                              key={`${question.id}-${key}`}
                            >
                              {name && (
                                <Tooltip title={name}>
                                  <StyledMetadataStandardSetName data-cy="itemBulkMetadataEditPanelStandardSetName">
                                    {name}
                                  </StyledMetadataStandardSetName>
                                </Tooltip>
                              )}
                              {standards.map((standard, index) => (
                                <StyledMetadataStandardRow
                                  key={
                                    standard?._id ||
                                    `${question.id}-${startIndex + index}`
                                  }
                                >
                                  <StandardValueDisplay
                                    standard={standard}
                                    romanLabel={
                                      showRomanLabels
                                        ? startIndex + index === 0
                                          ? 'i.'
                                          : 'ii.'
                                        : null
                                    }
                                  />
                                  <StyledStandardsEditButton
                                    type="button"
                                    data-cy="itemBulkMetadataEditPanelEditStandards"
                                    aria-label={`Edit standard ${
                                      standard?.identifier ||
                                      startIndex + index + 1
                                    }`}
                                    onClick={() =>
                                      handleOpenStandardsSelectionModal(
                                        question.id,
                                        standard?._id
                                      )
                                    }
                                  >
                                    <IconEditMetaData
                                      width={16}
                                      height={16}
                                      fill="#505e6f"
                                      aria-hidden
                                    />
                                  </StyledStandardsEditButton>
                                </StyledMetadataStandardRow>
                              ))}
                            </StyledMetadataStandardSetGroup>
                          )
                        )}
                        {hiddenStandardsCount > 0 && (
                          <StyledStandardMoreCount
                            as="span"
                            cursor="default"
                            data-cy="itemBulkMetadataEditPanelStandardsMoreCount"
                          >
                            +{hiddenStandardsCount} more
                          </StyledStandardMoreCount>
                        )}
                        {!selectedStandards.length && (
                          <StyledMetadataStandardsValueRow>
                            <StyledNoStandardsText data-cy="itemBulkMetadataEditPanelNoStandards">
                              No standards selected
                            </StyledNoStandardsText>
                            <StyledStandardsEditButton
                              type="button"
                              data-cy="itemBulkMetadataEditPanelEditStandards"
                              aria-label="Edit standards"
                              onClick={() =>
                                handleOpenStandardsSelectionModal(question.id)
                              }
                            >
                              <IconEditMetaData
                                width={16}
                                height={16}
                                fill="#505e6f"
                                aria-hidden
                              />
                            </StyledStandardsEditButton>
                          </StyledMetadataStandardsValueRow>
                        )}
                      </StyledMetadataStandardsList>
                    </>
                  )}
                </StyledMetadataStandardsContent>
              </StyledMetadataStandardsField>

              <StyledMetadataDifficultyField>
                <StyledMetadataFieldLabel htmlFor={difficultyFieldId}>
                  Difficulty
                </StyledMetadataFieldLabel>
                <StyledMetadataEditorSelect
                  id={difficultyFieldId}
                  data-cy="itemBulkMetadataEditPanelDifficulty"
                  placeholder="Select"
                  value={difficulty}
                  getPopupContainer={() => document.body}
                  dropdownStyle={{ zIndex: 2000 }}
                  dropdownMatchSelectWidth={false}
                  onChange={handleDifficultyChange(question.id)}
                >
                  {difficultyOptions.map((option) => (
                    <Select.Option key={option.value} value={option.value}>
                      {option.label}
                    </Select.Option>
                  ))}
                </StyledMetadataEditorSelect>
              </StyledMetadataDifficultyField>

              <StyledMetadataDokField>
                <StyledMetadataFieldLabel htmlFor={dokFieldId}>
                  DoK
                </StyledMetadataFieldLabel>
                <StyledMetadataEditorSelect
                  id={dokFieldId}
                  data-cy="itemBulkMetadataEditPanelDok"
                  placeholder="Select"
                  value={dok}
                  getPopupContainer={() => document.body}
                  dropdownStyle={{ zIndex: 2000 }}
                  dropdownMatchSelectWidth={false}
                  onChange={handleDokChange(question.id)}
                >
                  {dokOptions.map((option) => (
                    <Select.Option key={option.label} value={option.label}>
                      {option.label}
                    </Select.Option>
                  ))}
                </StyledMetadataEditorSelect>
              </StyledMetadataDokField>

              {showRubricActions && (
                <StyledMetadataRubricField data-cy="itemBulkMetadataEditPanelRubric">
                  <StyledMetadataFieldLabel>Rubric</StyledMetadataFieldLabel>
                  <StyledMetadataStandardsValueRow>
                    <Tooltip title={rubricName || 'View rubric'}>
                      <StyledRubricActionButton
                        type="button"
                        data-cy="itemBulkMetadataEditPanelViewRubric"
                        aria-label={
                          rubricName
                            ? `View rubric: ${rubricName}`
                            : 'View rubric'
                        }
                        disabled={rubricUiLoading}
                        onClick={() => handleViewRubric(question)}
                      >
                        <IconEye
                          width={14}
                          height={14}
                          color="#158463"
                          aria-hidden
                        />
                        VIEW
                      </StyledRubricActionButton>
                    </Tooltip>
                  </StyledMetadataStandardsValueRow>
                </StyledMetadataRubricField>
              )}
            </StyledMetadataEditorFields>

            <StyledMetadataEditorActions>
              <StyledMetadataEditorSaveButton
                type="button"
                data-cy="itemBulkMetadataEditPanelSave"
                aria-label="Save metadata"
                onClick={handleSave}
              >
                <IconCheck width={12} height={12} color="#158463" aria-hidden />
                Save
              </StyledMetadataEditorSaveButton>
              <StyledMetadataEditorClose
                type="button"
                data-cy="itemBulkMetadataEditPanelClose"
                aria-label="Close without saving"
                onClick={handlePanelClose}
              >
                <IconClose width={10} height={10} color="#151d34" aria-hidden />
              </StyledMetadataEditorClose>
            </StyledMetadataEditorActions>
          </MetadataEditorRow>
        )

        if (questionSubLabel) {
          return (
            <StyledMetadataEditorWithLabelRow
              key={question.id}
              flexProps={rowPadding}
            >
              <StyledMetadataQuestionSubLabel data-cy="itemBulkMetadataEditPanelQuestionIndex">
                ({questionSubLabel})
              </StyledMetadataQuestionSubLabel>
              {metadataEditorRow}
            </StyledMetadataEditorWithLabelRow>
          )
        }

        return (
          <FlexContainer
            key={question.id}
            flexDirection="column"
            width="100%"
            mt="16px"
            marginBottom="16px"
            flexProps={rowPadding}
          >
            {metadataEditorRow}
          </FlexContainer>
        )
      })}

      {showStandardsSelectionModal && standardsSelectionModalProps && (
        <StandardsModal
          defaultSubject={standardsSelectionModalProps.defaultSubject}
          defaultGrades={defaultGrades}
          defaultStandards={standardsSelectionModalProps.selectedStandards}
          defaultStandard={standardsSelectionModalProps.defaultStandard}
          pdlStandardProps={standardsSelectionModalProps.pdlStandardProps}
          visible={showStandardsSelectionModal}
          curriculums={allCurriculums}
          onApply={handleStandardsApply}
          setSubject={() => {}}
          onCancel={() => handleShowStandardsSelectionModal(false)}
          getCurriculumStandards={getCurriculumStandards}
          curriculumStandardsLoading={curriculumStandardsLoading}
          showAllInterestedCurriculums
          zIndex={2000}
        />
      )}

      {browseModal}
    </>
  )
}

ItemBulkMetadataEditPanel.propTypes = {
  item: PropTypes.object.isRequired,
  qid: PropTypes.string,
  test: PropTypes.object,
  suggestions: PropTypes.object,
  existingEditByQuestionId: PropTypes.object,
  updateSuggestion: PropTypes.func.isRequired,
  updateExistingEdit: PropTypes.func.isRequired,
  saveItem: PropTypes.func.isRequired,
  closeModal: PropTypes.func,
  onCancelExistingQuestionEdit: PropTypes.func,
  allCurriculums: PropTypes.array,
  interestedCurriculums: PropTypes.array,
  interestedSubjects: PropTypes.array,
  interestedGrades: PropTypes.array,
  getCurriculumStandards: PropTypes.func.isRequired,
  curriculumStandardsLoading: PropTypes.bool,
  updateRubricData: PropTypes.func.isRequired,
  setBulkSuggestions: PropTypes.func.isRequired,
  testItems: PropTypes.array,
  isExistingQuestionEdit: PropTypes.bool,
  questionSubLabel: PropTypes.string,
}

ItemBulkMetadataEditPanel.defaultProps = {
  qid: null,
  test: null,
  suggestions: {},
  existingEditByQuestionId: {},
  allCurriculums: [],
  interestedCurriculums: [],
  interestedSubjects: [],
  interestedGrades: [],
  curriculumStandardsLoading: false,
  testItems: [],
  isExistingQuestionEdit: false,
  questionSubLabel: null,
  closeModal: null,
  onCancelExistingQuestionEdit: null,
}

export default connect(
  (state) => ({
    test: getTestEntitySelector(state),
    suggestions: getBulkSuggestionsSelector(state),
    existingEditByQuestionId: getExistingEditByQuestionIdSelector(state),
    allCurriculums: getCurriculumsListSelector(state),
    interestedCurriculums: getAllInterestedCurriculumsSelector(state),
    interestedSubjects: getInterestedSubjectsSelector(state),
    interestedGrades: getInterestedGradesSelector(state),
    curriculumStandardsLoading: standardsSelector(state)?.loading,
    testItems: getTestItemsSelector(state),
  }),
  {
    updateSuggestion: updateBulkSuggestionAction,
    updateExistingEdit: updateExistingMetadataEditAction,
    saveItem: saveItemStandardAction,
    getCurriculumStandards: getDictStandardsForCurriculumAction,
    updateRubricData: updateRubricDataAction,
    setBulkSuggestions: setBulkSuggestionsAction,
  }
)(ItemBulkMetadataEditPanel)
